const PERMISSION_EVENTS = ['pointerdown', 'touchstart', 'keydown'];
let permissionRequestInFlight = null;

const getDefaultIconUrl = () => `${process.env.PUBLIC_URL || ''}/sonickart1.jpg`;
const getServiceWorkerReady = async () => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return null;
  }

  const existingRegistration = await navigator.serviceWorker.getRegistration();
  if (existingRegistration) {
    return existingRegistration;
  }

  try {
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((resolve) => {
        window.setTimeout(() => resolve(null), 1500);
      })
    ]);
  } catch (error) {
    console.error('Failed to resolve service worker registration:', error);
    return null;
  }
};

export const isBrowserNotificationSupported = () => (
  typeof window !== 'undefined' &&
  'Notification' in window
);

export const getBrowserNotificationPermission = () => {
  if (!isBrowserNotificationSupported()) {
    return 'unsupported';
  }

  return window.Notification.permission;
};

export const isBackgroundNotificationContext = () => {
  if (typeof document === 'undefined') {
    return false;
  }

  if (document.hidden) {
    return true;
  }

  if (typeof document.hasFocus === 'function') {
    return !document.hasFocus();
  }

  return false;
};

export const requestBrowserNotificationPermission = async () => {
  if (!isBrowserNotificationSupported()) {
    return 'unsupported';
  }

  if (window.Notification.permission !== 'default') {
    return window.Notification.permission;
  }

  if (permissionRequestInFlight) {
    return permissionRequestInFlight;
  }

  permissionRequestInFlight = (async () => {
    try {
      return await window.Notification.requestPermission();
    } catch (error) {
      console.error('Failed to request browser notification permission:', error);
      return window.Notification.permission || 'default';
    } finally {
      permissionRequestInFlight = null;
    }
  })();

  try {
    return await permissionRequestInFlight;
  } catch (error) {
    return window.Notification.permission || 'default';
  }
};

export const initializeBrowserNotifications = (onPermissionChange) => {
  if (typeof window === 'undefined' || !isBrowserNotificationSupported()) {
    return () => {};
  }

  if (window.Notification.permission !== 'default') {
    if (typeof onPermissionChange === 'function') {
      onPermissionChange(window.Notification.permission);
    }
    return () => {};
  }

  const removePermissionListeners = () => {
    PERMISSION_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, requestOnFirstInteraction);
    });
  };

  const requestOnFirstInteraction = async () => {
    if (window.Notification.permission !== 'default') {
      removePermissionListeners();
      return;
    }

    removePermissionListeners();
    const nextPermission = await requestBrowserNotificationPermission();
    if (typeof onPermissionChange === 'function') {
      onPermissionChange(nextPermission);
    }
  };

  PERMISSION_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, requestOnFirstInteraction, { passive: true });
  });

  return () => {
    removePermissionListeners();
  };
};

export const showBrowserNotification = async ({
  title,
  body,
  tag,
  data,
  icon,
  requireInteraction = false
}) => {
  if (!title || !isBrowserNotificationSupported() || window.Notification.permission !== 'granted') {
    return false;
  }

  const options = {
    body: body || '',
    tag: tag || 'sonickart-alert',
    data: {
      url: '/admin',
      ...data
    },
    icon: icon || getDefaultIconUrl(),
    badge: icon || getDefaultIconUrl(),
    requireInteraction,
    renotify: true
  };

  try {
    const registration = await getServiceWorkerReady();
    if (registration) {
      await registration.showNotification(title, options);
      return true;
    }

    const notification = new window.Notification(title, options);
    notification.onclick = () => {
      window.focus();
      if (options.data?.url) {
        window.location.assign(options.data.url);
      }
      notification.close();
    };
    return true;
  } catch (error) {
    console.error('Failed to show browser notification:', error);
    return false;
  }
};
